import { z } from 'zod';

import { ApiError } from '@/lib/api';
import { errorAdvice } from '@/lib/error-advice';

// Verificación de integridad de la firma del sello (spec 2026-10-14 §6): contrato de
// GET /signatures/:id/verify y su lectura en palabras (UX-COPY-01, UX-ERR-01).

const verifyResultSchema = z.object({
  valid: z.boolean(),
  snapshotHashMatches: z.boolean(),
  chainHashMatches: z.boolean(),
  linkMatches: z.boolean(),
  columnsMatch: z.boolean(),
  ownColumnsMatch: z.boolean(),
  position: z.number().int().nonnegative().nullable(),
  chainValid: z.boolean(),
  brokenAt: z.number().int().nonnegative().nullable(),
});
export type VerifyResult = z.infer<typeof verifyResultSchema>;

export const verifyResponseSchema = z.object({
  success: z.literal(true),
  data: verifyResultSchema,
});

export interface VerifyOutcome {
  valid: boolean;
  title: string;
  /** Qué falló, una frase por falla (vacío si es íntegra). */
  problems: string[];
  /** Qué significa o qué hacer. */
  detail: string;
}

// Qué falló, atribuido a la firma correcta: `brokenAt` es la primera firma rota de la
// cadena y `position` la del sello. Si la rota es una anterior, el eslabón propio deja de
// cuadrar sin que la firma del sello esté alterada.
function problemsOf(r: VerifyResult): string[] {
  const out: string[] = [];
  if (!r.snapshotHashMatches) {
    out.push('El contenido aprobado no coincide con su huella.');
  }
  const otherBroken = !r.chainValid && r.brokenAt !== null && r.brokenAt !== r.position;
  const earlierBroken =
    otherBroken && r.brokenAt !== null && r.position !== null && r.brokenAt < r.position;
  // Un eslabón propio que no cuadra solo por una firma anterior alterada no es de esta.
  if (!r.chainHashMatches || (!r.linkMatches && !earlierBroken)) {
    out.push('La cadena de firmas está rota en esta firma.');
  }
  if (otherBroken && r.brokenAt !== null) {
    out.push(
      `Otra firma del documento está alterada: la número ${r.brokenAt + 1}, en orden de firma.`,
    );
  }
  if (!r.ownColumnsMatch) {
    out.push(
      'Los datos del firmante, la fecha o la versión de esta firma no coinciden con lo firmado.',
    );
  } else if (!r.columnsMatch) {
    out.push('Los datos de otra firma del documento no coinciden con lo que se firmó.');
  }
  return out;
}

export function verifyOutcome(r: VerifyResult): VerifyOutcome {
  if (r.valid) {
    return {
      valid: true,
      title: 'Firma íntegra',
      problems: [],
      detail: 'El contenido aprobado y la cadena de firmas coinciden con lo registrado.',
    };
  }
  const problems = problemsOf(r);
  return {
    valid: false,
    title: 'La verificación encontró una alteración',
    problems:
      problems.length > 0
        ? problems
        : ['La firma no coincide con la cadena de firmas del documento.'],
    detail: 'Avisa al administrador del sistema: el registro pudo modificarse fuera del QMS.',
  };
}

export function verifyErrorText(err: unknown): string {
  if (!(err instanceof ApiError)) {
    return `No se pudo verificar la firma. ${errorAdvice(err)}`;
  }
  if (err.status === 404) {
    return 'La firma ya no existe en esta empresa. Recarga la ficha.';
  }
  if (err.status === 403) {
    return 'No tienes permiso para verificar firmas.';
  }
  return 'No se pudo verificar la firma. Intenta de nuevo y, si persiste, contacta a soporte.';
}
